import * as React from "react";
import { fitFloorLabelSize } from "@eduviaos/shared";
import { cn } from "@eduviaos/ui-kit";
import type {
  FloorFeature,
  FloorPlanBody,
  FloorZoneShape,
  SeatOccupancyStatus,
} from "@/lib/seating/floorPlan";
import { OCCUPANCY_STATUS_LABELS } from "@/lib/seating/floorPlan";
import { SEAT_STATUS_SWATCH } from "@/lib/seating/floorStyles";

/**
 * Phase SC-22c — SVG drawing of one floor plan. The viewBox is the plan's
 * own unit space (10 cm per unit), so every caller works in plan units and
 * the browser scales it to the container.
 *
 * Status colours follow the `<StatusPill>` palette (emerald = available,
 * amber = held, sky = in progress, rose = blocked) so a seat reads the same
 * here as a pill does in a table. The theme primary marks the selection.
 */

const FEATURE_CLASSES: Record<FloorFeature["kind"], string> = {
  entrance: "fill-emerald-500/20 stroke-emerald-600/70",
  window: "fill-sky-400/30 stroke-sky-500/70",
  wall: "fill-foreground/70 stroke-foreground/70",
  ac: "fill-sky-500/15 stroke-sky-600/60",
  washroom: "fill-muted stroke-border",
  reception: "fill-amber-500/15 stroke-amber-600/60",
  pillar: "fill-foreground/40 stroke-foreground/50",
};

const FEATURE_LABELS: Record<FloorFeature["kind"], string> = {
  entrance: "Entrance",
  window: "Window",
  wall: "Wall",
  ac: "AC",
  washroom: "Washroom",
  reception: "Reception",
  pillar: "Pillar",
};

/** The floor outline, rooms, and fixed features — everything but seats. */
export function FloorBackdrop({
  plan,
  selectedZoneIndex,
  onZonePointerDown,
}: {
  plan: FloorPlanBody;
  selectedZoneIndex?: number | null;
  onZonePointerDown?: (index: number, event: React.PointerEvent<SVGRectElement>) => void;
}) {
  const labelSize = Math.max(4, Math.min(plan.width, plan.height) / 40);
  return (
    <g>
      <rect
        x={0}
        y={0}
        width={plan.width}
        height={plan.height}
        className="fill-card stroke-border"
        strokeWidth={0.6}
        rx={1}
      />
      {plan.zones.map((zone: FloorZoneShape, i) => (
        <g key={`zone-${zone.zone_id ?? zone.label}-${i}`}>
          <rect
            x={zone.x}
            y={zone.y}
            width={zone.w}
            height={zone.h}
            rx={1.5}
            data-testid={`floor-zone-${i}`}
            onPointerDown={onZonePointerDown ? (e) => onZonePointerDown(i, e) : undefined}
            className={cn(
              "fill-primary/5 stroke-primary/40 transition-[fill,stroke] duration-150 ease-out",
              onZonePointerDown && "cursor-move",
              selectedZoneIndex === i && "fill-primary/10 stroke-primary",
            )}
            strokeWidth={selectedZoneIndex === i ? 0.8 : 0.5}
            strokeDasharray={selectedZoneIndex === i ? undefined : "2 1.5"}
          />
          <text
            x={zone.x + 2}
            y={zone.y + labelSize + 1}
            fontSize={labelSize}
            className="fill-muted-foreground pointer-events-none select-none font-medium"
          >
            {zone.label}
          </text>
        </g>
      ))}
      {plan.features.map((f, i) => {
        const label = f.label ?? FEATURE_LABELS[f.kind];
        const fontSize = fitFloorLabelSize(label, f.w, f.h);
        return (
          <g
            key={`feature-${f.kind}-${i}`}
            transform={
              f.rotation ? `rotate(${f.rotation} ${f.x + f.w / 2} ${f.y + f.h / 2})` : undefined
            }
            className="pointer-events-none"
          >
            <rect
              x={f.x}
              y={f.y}
              width={f.w}
              height={f.h}
              rx={0.6}
              className={FEATURE_CLASSES[f.kind]}
              strokeWidth={0.4}
            >
              <title>{label}</title>
            </rect>
            {fontSize !== null ? (
              <text
                x={f.x + f.w / 2}
                y={f.y + f.h / 2}
                fontSize={fontSize}
                textAnchor="middle"
                dominantBaseline="central"
                data-testid={`floor-feature-label-${i}`}
                className="fill-muted-foreground select-none"
              >
                {label}
              </text>
            ) : null}
          </g>
        );
      })}
    </g>
  );
}

export function OccupancyLegend({
  counts,
  className,
}: {
  counts?: Partial<Record<SeatOccupancyStatus, number>>;
  className?: string;
}) {
  const order: SeatOccupancyStatus[] = ["free", "booked", "occupied", "fixed", "blocked"];
  return (
    <ul
      aria-label="Seat colours"
      className={cn("flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs", className)}
    >
      {order.map((status) => (
        <li key={status} className="text-muted-foreground inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={cn("inline-block size-3 rounded-[3px] border", SEAT_STATUS_SWATCH[status])}
          />
          <span>{OCCUPANCY_STATUS_LABELS[status]}</span>
          {counts ? (
            <span className="text-foreground font-medium tabular-nums">{counts[status] ?? 0}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
